import { useState, useEffect } from 'react';
import { getActiveFeatures, getAllFeatures } from '../firebase/features';

// Basic features hook - returns array of features
export const useFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get active features
        const result = await getActiveFeatures();
        if (result.success) {
          const featuresArray = result.data.map(feature => ({
            id: feature.id,
            label: feature.label,
            source: feature.source || 'manual',
            confidence: feature.confidence || null,
            active: true
          }))
          .sort((a, b) => {
            // Sort by source (auto first) then by label
            if (a.source === 'auto' && b.source !== 'auto') return -1;
            if (a.source !== 'auto' && b.source === 'auto') return 1;
            return a.label.localeCompare(b.label);
          });

          setFeatures(featuresArray);
          setError(null);
        } else {
          throw new Error(result.error || 'Failed to load features');
        }
      } catch (err) {
        console.error('Error loading features:', err);
        setError(err.message);
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
        }
      } finally {
        setLoading(false);
      }
    };

    loadFeatures();
  }, [retryCount]);

  return { features, loading, error };
};

// Enhanced features hook - returns object map of features with metadata
export const useFeaturesWithMetadata = () => {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await getAllFeatures();
        if (result.success) {
          // Process features
          const processedFeatures = Object.entries(result.data)
            .reduce((acc, [id, feature]) => {
              acc[id] = {
                id,
                label: feature.label,
                source: feature.source || 'manual',
                category: feature.category,
                confidence: feature.confidence || null,
                active: feature.active ?? true,
                createdAt: feature.createdAt || null,
                updatedAt: feature.updatedAt || null
              };
              return acc;
            }, {});

          setFeatures(processedFeatures);
          setError(null);
        } else {
          throw new Error(result.error || 'Failed to load features');
        }
      } catch (err) {
        console.error('Error loading features:', err);
        setError(err.message);
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
        }
      } finally {
        setLoading(false);
      }
    };

    loadFeatures();
  }, [retryCount]);

  return { features, loading, error };
};
